import React, { useState, useEffect } from 'react';
import { addDays, differenceInDays } from 'date-fns';
import DateRangePicker from '../components/DateRangePicker';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // Auth hook'u
import { db } from '../utils/firebase'; // Firestore db
import { doc, getDoc } from 'firebase/firestore'; // Firestore fonksiyonları
import { unlockAchievement } from '../services/achievementService';
import { createPlan } from '../services/api';

const provinces = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin', 'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Isparta', 'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli', 'Kırşehir', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş', 'Mardin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop', 'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa', 'Uşak', 'Van', 'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırklareli', 'Batman', 'Şırnak', 'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis', 'Osmaniye', 'Düzce'
];

export default function StartPlan() {
  const navigate = useNavigate();
  const { currentUser } = useAuth(); // Kullanıcıyı al
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ origin: '', destination: '', budget: '', travelers: '' });
  const [dateRange, setDateRange] = useState({ start: new Date(), end: addDays(new Date(), 5) });
  const [showCalendar, setShowCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const destinationFromQuery = searchParams.get('destination');
    if (destinationFromQuery && provinces.includes(destinationFromQuery)) {
      setForm(prevForm => ({ ...prevForm, destination: destinationFromQuery }));
    }
  }, [searchParams]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const duration = differenceInDays(dateRange.end, dateRange.start) + 1;

      let personalizationData = null;

      if (currentUser) {
        const prefRef = doc(db, 'userPreferences', currentUser.uid);
        const prefSnap = await getDoc(prefRef);
        if (prefSnap.exists()) {
          personalizationData = prefSnap.data();
        }
      }

      const requestBody = {
        origin: form.origin,
        destination: form.destination,
        budget: form.budget,
        travelers: form.travelers,
        duration,
        personalization: personalizationData,
      };

      const data = await createPlan(requestBody);

      // Başarım Kilidi Aç: İlk Plan
      if (currentUser) {
        unlockAchievement('first-plan').then(unlocked => {
          if (unlocked) {
            console.log("Başarım açıldı: İlk Planını Yaptın!");
          }
        }).catch(err => console.error("Başarım hatası:", err));
      }

      navigate('/plan-result', {
        state: {
          plan: data.plan,
          query: { ...form, duration }
        }
      });
    } catch (err) {
      console.error('Plan generation error:', err);
      setError(err.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className="min-h-screen bg-background-light text-text-dark dark:text-white dark:bg-gray-900">
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold">Birlikte Seyahat Planlayalım</h1>
          <p className="text-gray-600">Tercihlerini söyle, gerisini yapay zekaya bırak.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-xl shadow border border-gray-200 dark:border-gray-700 space-y-6">
          <datalist id="tr-provinces">{provinces.map(p => <option key={p} value={p} />)}</datalist>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm mb-1">Nereden?</label>
              <input list="tr-provinces" name="origin" value={form.origin} onChange={handleChange} placeholder="İl yazın veya seçin" className="w-full h-12 px-4 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-transparent focus:border-primary outline-none" />
            </div>
            <div className="relative">
              <label className="block text-sm mb-1">Nereye?</label>
              <input list="tr-provinces" name="destination" value={form.destination} onChange={handleChange} placeholder="İl yazın veya seçin" className="w-full h-12 px-4 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-transparent focus:border-primary outline-none" />
            </div>
          </div>

          {provinces.includes(form.destination) && (
            <div className="text-center bg-blue-50 dark:bg-gray-700/50 p-3 rounded-lg border border-blue-200 dark:border-gray-600">
              <p className="text-sm text-gray-800 dark:text-gray-200">
                📍 {form.destination} için etkinlikler, doğa güzellikleri ve daha fazlasını keşfetmek için{' '}
                <Link to={`/blog?city=${form.destination.toLowerCase()}`} className="font-bold text-blue-600 dark:text-blue-400 hover:underline">
                  {form.destination} İl Rehberi'ne
                </Link>
                {' '}göz atın.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm mb-1">Bütçe (Kişi Başı)</label>
              <input type="number" name="budget" value={form.budget} onChange={handleChange} placeholder="20000" className="w-full h-12 px-4 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-transparent focus:border-primary outline-none" />
            </div>
            <div className="relative">
              <label className="block text-sm mb-1">Süre</label>
              <input readOnly value={`${differenceInDays(dateRange.end, dateRange.start) + 1} gün`} onClick={() => setShowCalendar(!showCalendar)} className="w-full h-12 px-4 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-transparent focus:border-primary outline-none cursor-pointer" />
              {showCalendar && (
                <div className="absolute z-10 mt-2">
                  <DateRangePicker value={dateRange} onChange={setDateRange} />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm mb-1">Kişi</label>
              <input type="number" name="travelers" value={form.travelers} onChange={handleChange} placeholder="2" className="w-full h-12 px-4 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-transparent focus:border-primary outline-none" />
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-lg font-bold text-white bg-primary hover:opacity-90 transition transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Plan Oluşturuluyor...' : 'Yapay Zeka ile Plan Oluştur'}
          </button>
        </form>
      </div>
    </main>
  );
}
